import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { MoreHorizontal, Download } from 'lucide-react';

const Container = styled.div`
  display: flex;
`;

const Sidebar = styled.div`
  width: 35%;
  border-right: 1px solid #e5e7eb;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const SidebarHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
  color: #111827;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ChatItem = styled.div<{ active?: boolean }>`
  padding: 16px;
  border-bottom: 1px solid #f3f4f6;
  background: ${props => props.active ? '#f9fafb' : 'transparent'};
  cursor: pointer;
  border-left: 3px solid ${props => props.active ? '#7c3aed' : 'transparent'};
  
  &:hover {
    background: #f9fafb;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  
  span {
    font-weight: 600;
    font-size: 14px;
    color: #111827;
  }
`;

const Meta = styled.div`
  font-size: 12px;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #ffffff;
`;

const Header = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const ChatArea = styled.div`
  flex: 1;
  padding: 24px;
  background: #ffffff;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const MessageBubble = styled(motion.div) <{ isUser?: boolean }>`
  max-width: 80%;
  align-self: ${props => props.isUser ? 'flex-end' : 'flex-start'};
  
  .meta {
    font-size: 11px;
    color: #9ca3af;
    margin-bottom: 6px;
    text-align: ${props => props.isUser ? 'right' : 'left'};
  }
  
  .bubble {
    padding: 14px 18px;
    border-radius: 12px;
    background: ${props => props.isUser ? '#eff6ff' : '#fffbeb'};
    color: #1f2937;
    font-size: 14px;
    line-height: 1.6;
    border: 1px solid ${props => props.isUser ? '#dbeafe' : '#fef3c7'};
  }
`;

const Flag = ({ country }: { country: string }) => (
  <img
    src={`https://flagcdn.com/20x15/${country}.png`}
    alt={country}
    style={{ width: 20, height: 15, borderRadius: 2 }}
  />
);

const TranscriptsVisual: React.FC = () => {
  return (
    <Container>
      <Sidebar>
        <SidebarHeader>
          Freddie Follicle
          <MoreHorizontal size={16} color="#9ca3af" />
        </SidebarHeader>
        <ChatItem active>
          <UserInfo>
            <Flag country="gb" />
            <span>81.99.250.15</span>
          </UserInfo>
          <Meta>
            <span>mobile iOS Mobile Chrome</span>
            <span>Nov 23, 2025, 11:54 AM</span>
          </Meta>
        </ChatItem>
        <ChatItem>
          <UserInfo>
            <Flag country="ee" />
            <span>82.131.48.180</span>
          </UserInfo>
          <Meta>
            <span>Desktop macOS Chrome</span>
            <span>Nov 23, 2025, 03:47 AM</span>
          </Meta>
        </ChatItem>
        <ChatItem>
          <UserInfo>
            <Flag country="no" />
            <span>109.145.51.219</span>
          </UserInfo>
          <Meta>
            <span>mobile iOS Mobile Safari</span>
            <span>Nov 23, 2025, 02:47 AM</span>
          </Meta>
        </ChatItem>
        <ChatItem>
          <UserInfo>
            <Flag country="us" />
            <span>145.40.129.217</span>
          </UserInfo>
          <Meta>
            <span>mobile Android Mobile Chrome</span>
            <span>Nov 23, 2025, 02:19 AM</span>
          </Meta>
        </ChatItem>
      </Sidebar>

      <MainContent>
        <Header>
          <Title>Conversation Details</Title>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: '#6b7280', cursor: 'pointer' }}>
            <Download size={14} />
            Download this conversation
          </div>
        </Header>
        <ChatArea>
          <MessageBubble isUser initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
            <div className="meta">User • 11/23/2025, 2:47:46 AM</div>
            <div className="bubble">
              when will the minoxidil foam be back in stock
            </div>
          </MessageBubble>

          <MessageBubble initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}>
            <div className="meta">Assistant • 11/23/2025, 2:47:46 AM</div>
            <div className="bubble">
              I'm unable to find any relevant information on this. Please [contact our team](https://hairrepairclinic.co.uk/contact/) for help.
            </div>
          </MessageBubble>
        </ChatArea>
      </MainContent>
    </Container>
  );
};

export default TranscriptsVisual;
